import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Images, Plus, Trash2, Loader2, Upload, ArrowUp, ArrowDown, Film } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { mediaUrl, MediaThumb } from "./ProjectGallery";

const CATEGORIES = ["Lawn Maintenance", "Plant Installation", "Hardscaping", "Drainage Solutions", "Full Property", "Other"];
const STAGES = [["before", "Before"], ["during", "During"], ["after", "After"]];

export default function GalleryManager() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", category: CATEGORIES[0], description: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // `${id}-${stage}`

  const load = useCallback(async () => {
    try { const { data } = await api.get("/gallery"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createProject = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Project title is required."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("category", form.category); fd.append("description", form.description);
      await api.post("/gallery", fd);
      toast.success("Project created — now add photos & videos below.");
      setForm({ title: "", category: CATEGORIES[0], description: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addMedia = async (id, stage, file) => {
    if (!file) return;
    setUploading(`${id}-${stage}`);
    try {
      const fd = new FormData(); fd.append("stage", stage); fd.append("file", file);
      await api.post(`/gallery/${id}/media`, fd);
      toast.success("Uploaded."); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setUploading(null); }
  };
  const removeMedia = async (id, path) => {
    try { await api.delete(`/gallery/${id}/media`, { data: { path } }); load(); }
    catch (err) { toast.error("Failed to remove"); }
  };
  const removeProject = async (id) => {
    try { await api.delete(`/gallery/${id}`); toast.success("Project deleted"); load(); }
    catch (err) { toast.error("Failed"); }
  };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/gallery/reorder", { order: arr.map((p) => p.id) }); } catch { load(); }
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center gap-2 mb-6"><Images className="w-6 h-6 text-emerald" /><h2 className="font-display text-2xl font-600 text-charcoal">Our Work — Projects</h2></div>

      <form onSubmit={createProject} className="bg-white border border-border rounded-sm p-6 mb-10 space-y-4" data-testid="project-create-form">
        <div className="flex items-center gap-2 text-charcoal font-600"><Plus className="w-4 h-4 text-emerald" /> Create a new project</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="project-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder='e.g. French Drain Installation - Pooler'
            className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" />
          <select data-testid="project-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-emerald/40">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <textarea data-testid="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Short description (optional)"
          className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" />
        <button type="submit" disabled={saving} data-testid="project-create-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-6 py-3 rounded-sm font-600 hover:bg-emerald-dark transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Create Project
        </button>
      </form>

      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : items.length === 0 ? (
        <p className="text-olive text-center py-10" data-testid="gallery-manager-empty">No projects yet. Create your first above.</p>
      ) : (
        <div className="space-y-6">
          {items.map((p, idx) => (
            <div key={p.id} className="bg-white border border-border rounded-sm p-5" data-testid={`project-manage-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <span className="text-xs font-600 text-gold">{p.category}</span>
                  <h3 className="font-600 text-charcoal">{p.title}</h3>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => move(idx, -1)} className="p-2 rounded-sm text-charcoal/60 hover:bg-border" data-testid={`project-up-${p.id}`}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} className="p-2 rounded-sm text-charcoal/60 hover:bg-border" data-testid={`project-down-${p.id}`}><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => removeProject(p.id)} className="p-2 rounded-sm text-red-600 hover:bg-red-50" data-testid={`project-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {STAGES.map(([stage, label]) => {
                  const stageMedia = (p.media || []).filter((m) => m.stage === stage);
                  const key = `${p.id}-${stage}`;
                  return (
                    <div key={stage} className="border border-border rounded-sm p-3">
                      <p className="text-sm font-600 text-charcoal mb-2">{label} <span className="text-olive font-400">({stageMedia.length})</span></p>
                      <div className="grid grid-cols-3 gap-1.5 mb-2">
                        {stageMedia.map((m) => (
                          <div key={m.path} className="relative group aspect-square rounded-sm overflow-hidden" data-testid={`media-${p.id}`}>
                            <MediaThumb item={m} className="w-full h-full" />
                            <button onClick={() => removeMedia(p.id, m.path)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <label className="flex items-center justify-center gap-1.5 text-xs font-600 text-emerald border border-dashed border-emerald/40 rounded-sm py-2 cursor-pointer hover:bg-emerald/5" data-testid={`upload-${stage}-${p.id}`}>
                        {uploading === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {stage === "during" ? <>Photo / <Film className="w-3 h-3" /> Video</> : "Add photo"}
                        <input type="file" accept={stage === "during" ? "image/*,video/mp4,video/quicktime" : "image/*,video/mp4"} className="hidden"
                          onChange={(e) => { addMedia(p.id, stage, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
